/**
 * Lightweight performance instrumentation for dev baseline and after comparisons.
 * Only active when import.meta.env.DEV is true.
 *
 * Comment interpréter les sorties :
 * - perfLogSummary() : log JSON avec marks (événements datés) et counters (cumuls).
 * - perfLogConclusion() : résumé lisible (TTI, compteurs).
 *
 * Ce qu’on peut en conclure :
 * - elapsedMs / timeToMount : temps jusqu’au premier affichage (~75–100 ms = bon).
 * - contextLoads : nombre d’appels loadUserHouseholds ; 1–2 = normal (cache efficace).
 * - routeChanges : nombre de navigations ; comparer avant/après refacto.
 * - childPageLoads : chargements enfant ; idéalement proche du nombre de vues enfant visitées.
 * - supabaseCalls : appels réseau Supabase (fetch) ; 0 = patch non appliqué ou pas de requêtes.
 */

const isDev = typeof import.meta !== 'undefined' && import.meta.env?.DEV === true;

export interface PerfMark {
  name: string;
  timestamp: number;
  elapsedSinceStart: number;
}

export interface PerfCounters {
  supabaseCalls: number;
  routeChanges: number;
  contextLoads: number;
  childPageLoads: number;
  [key: string]: number;
}

const startTime = typeof performance !== 'undefined' ? performance.now() : Date.now();
const marks: PerfMark[] = [];
const counters: PerfCounters = {
  supabaseCalls: 0,
  routeChanges: 0,
  contextLoads: 0,
  childPageLoads: 0,
};

/**
 * Journal glissant des derniers appels Supabase, avec leur chemin.
 * Le compteur `supabaseCalls` dit COMBIEN d'appels partent ; il ne dit pas
 * LESQUELS, donc il ne permet pas de savoir quoi couper. On garde les derniers
 * chemins pour que le résumé de route puisse afficher exactement ce qui a été
 * tiré depuis la navigation précédente.
 */
interface PerfCall { t: number; path: string }
const recentCalls: PerfCall[] = [];
const RECENT_CALLS_MAX = 60;
let lastSummaryAt = startTime;

/** Chemins Supabase appelés depuis `sinceMs` (défaut : depuis le dernier résumé). */
export function perfGetRecentCalls(sinceMs?: number): string[] {
  const from = sinceMs ?? lastSummaryAt;
  return recentCalls.filter((c) => c.t >= from).map((c) => c.path);
}

export function perfMark(name: string): void {
  if (!isDev) return;
  const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
  marks.push({
    name,
    timestamp: now,
    elapsedSinceStart: now - startTime,
  });
}

export function perfIncrement(counterName: keyof PerfCounters, amount = 1): void {
  if (!isDev) return;
  if (counters[counterName] === undefined) {
    counters[counterName] = 0;
  }
  counters[counterName] += amount;
}

export function perfGetStartTime(): number {
  return startTime;
}

export function perfGetMarks(): PerfMark[] {
  return [...marks];
}

export function perfGetCounters(): PerfCounters {
  return { ...counters };
}

export function perfGetElapsedMs(): number {
  const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
  return now - startTime;
}

/**
 * Log a structured summary to console (dev only). Call after app is interactive or on route change.
 */
export function perfLogSummary(label?: string): void {
  if (!isDev) return;
  const elapsed = perfGetElapsedMs();
  // Seulement la queue des marks : le tableau grandit à chaque navigation et
  // le sérialiser en entier à chaque route noyait la console (26 entrées après
  // une minute d'usage) et gonflait le breadcrumb Sentry correspondant. Les
  // marks de boot restent lisibles via perfGetMarks() si besoin.
  const TAIL = 6;
  const tail = marks.slice(-TAIL).map((m) => ({ name: m.name, ms: Math.round(m.elapsedSinceStart) }));
  const summary = {
    label: label || 'summary',
    elapsedMs: Math.round(elapsed),
    marks: tail,
    marksTotal: marks.length,
    counters: { ...counters },
  };
  console.log('[perf]', JSON.stringify(summary));
}

const COUNTER_LABELS: Record<string, string> = {
  supabaseCalls: 'Appels Supabase (fetch)',
  routeChanges: 'Changements de route',
  contextLoads: 'Chargements contexte (loadUserHouseholds)',
  childPageLoads: 'Chargements page enfant (loadChildFromURL / init)',
};

/**
 * Log a short, human-readable conclusion (dev only). Call when you want to quickly interpret the session.
 */
export function perfLogConclusion(label?: string): void {
  if (!isDev) return;
  const elapsed = perfGetElapsedMs();
  const elapsedSec = Math.round(elapsed / 1000);
  const timeToMount = marks.find((m) => m.name === 'main-after-mount')?.elapsedSinceStart;
  const lastContextEnd = [...marks].reverse().find((m) => m.name === 'app-context-load-end')?.elapsedSinceStart;

  const lines: string[] = [
    '--- [perf] Résumé ---',
    `Contexte: ${label ?? 'session'}`,
    `Temps total écoulé: ${elapsedSec}s`,
    `Nombre d’événements enregistrés (marks): ${marks.length}`,
    '',
    'Temps clés:',
    timeToMount != null ? `  - Time to mount (TTI): ${Math.round(timeToMount)}ms` : '  - Time to mount: (non enregistré)',
    lastContextEnd != null ? `  - Dernier app-context-load-end: ${Math.round(lastContextEnd)}ms` : null,
    '',
    'Compteurs:',
    ...Object.entries(counters)
      .filter(([, v]) => typeof v === 'number')
      .map(([k, v]) => `  - ${COUNTER_LABELS[k] ?? k}: ${v}`),
    '',
    'Interprétation:',
    timeToMount != null && timeToMount < 200 ? '  - TTI: bon (< 200ms)' : timeToMount != null ? `  - TTI: ${Math.round(timeToMount)}ms` : null,
    counters.contextLoads <= 2 ? '  - Contexte: cache efficace (1–2 chargements)' : `  - Contexte: ${counters.contextLoads} chargements`,
    counters.supabaseCalls === 0 ? '  - Supabase: 0 appel compté (vérifier VITE_SUPABASE_URL ou patch fetch)' : `  - Supabase: ${counters.supabaseCalls} appels`,
    `  - Routes: ${counters.routeChanges} navigation(s), enfant: ${counters.childPageLoads} chargement(s)`,
    // Le point qui manquait pour trancher : le compteur global dit COMBIEN,
    // pas LESQUELS. Sans les chemins, impossible de savoir quoi couper.
    ...(() => {
      const since = perfGetRecentCalls();
      if (!since.length) return ['  - Depuis la route précédente: aucun appel Supabase'];
      const tally = since.reduce<Record<string, number>>((acc, p) => {
        acc[p] = (acc[p] ?? 0) + 1;
        return acc;
      }, {});
      return [
        `  - Depuis la route précédente: ${since.length} appel(s) Supabase`,
        ...Object.entries(tally)
          .sort((a, b) => b[1] - a[1])
          .map(([p, n]) => `      ${n}x ${p}`),
      ];
    })(),
    '---',
  ].filter((x): x is string => x != null);

  console.log(lines.join('\n'));
  // Fenêtre suivante : on ne veut que les appels de la PROCHAINE navigation.
  lastSummaryAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
}

/**
 * Install a fetch patch to count and time Supabase requests (dev only).
 * Call once from main.ts before any Supabase usage.
 * If supabaseUrl is empty, counts any request whose URL contains "supabase".
 */
export function perfInstallFetchPatch(supabaseUrl?: string): void {
  if (!isDev || typeof window === 'undefined' || !window.fetch) return;
  const originalFetch = window.fetch;
  const matchUrl = (url: string) =>
    (supabaseUrl && url.includes(supabaseUrl)) || (!supabaseUrl && url.includes('supabase'));
  window.fetch = function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const url = typeof input === 'string' ? input : input instanceof Request ? input.url : input.toString();
    if (matchUrl(url)) {
      perfIncrement('supabaseCalls');
      // Garde le chemin (sans l'hôte ni le querystring, souvent énorme) pour
      // pouvoir nommer les appels dans le résumé de route.
      try {
        const u = new URL(url);
        // Signature = table + NOMS des filtres (jamais les valeurs : ni données
        // personnelles dans les logs, ni bruit illisible). Sans elle, « 4x tasks »
        // est ambigu : 4 requêtes identiques (dédoublonnables) ou 4 requêtes
        // différentes sur la même table (à regrouper côté appelants). Ces deux
        // diagnostics n'appellent pas du tout le même correctif.
        const keys = [...u.searchParams.keys()].filter((k) => k !== 'select').sort();
        const sig = keys.length ? `?${keys.join('&')}` : '';
        recentCalls.push({ t: performance.now(), path: u.pathname.replace('/rest/v1/', '') + sig });
        if (recentCalls.length > RECENT_CALLS_MAX) recentCalls.shift();
      } catch { /* URL non parsable : on ignore, ce n'est qu'un diagnostic */ }
      const start = performance.now();
      return originalFetch.call(this, input, init).then((res) => {
        const duration = Math.round(performance.now() - start);
        if (duration > 500) {
          console.warn(`[perf] Slow Supabase request: ${duration}ms`, url.slice(0, 80));
        }
        return res;
      });
    }
    return originalFetch.call(this, input, init);
  };
}

export function perfReset(): void {
  marks.length = 0;
  Object.keys(counters).forEach((k) => {
    counters[k] = 0;
  });
}
